import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDevices } from '../store/devices';
import { useLiveEvents } from '../hooks/useLiveEvents';
import { api } from '../store/auth';
import toast from 'react-hot-toast';
import BackBtn from '../components/BackBtn';

export default function NetworkScanner() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { sendCommand } = useDevices();
  const [hosts, setHosts] = useState([]);
  const [scanning, setScanning] = useState(false);

  // SMB shares & file browser
  const [selectedHost, setSelectedHost] = useState(null);
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [browsePath, setBrowsePath] = useState('');
  const [browseEntries, setBrowseEntries] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState('');
  const [fileStatus, setFileStatus] = useState('');

  // Upload to network path
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');

  useLiveEvents((event) => {
    if (event.device_id !== deviceId) return;

    if (event.event === 'network_scan_result') {
      setHosts(event.devices || []);
      setScanning(false);
      toast.success(`Scan complete — ${event.devices?.length || 0} hosts found`);
    }
    if (event.event === 'network_shares_result') {
      setShares(event.shares || []);
      setSharesLoading(false);
      if (event.shares?.length === 0) toast('No accessible shares found', { icon: 'ℹ️' });
    }
    if (event.event === 'network_browse_result') {
      setBrowseEntries(event.entries || []);
      setBrowsePath(event.path || '');
      setBrowseError(event.error || '');
      setBrowseLoading(false);
    }
    if (event.event === 'file_ready') {
      setFileStatus(`Downloaded: ${event.filename} (${(event.size/1024).toFixed(1)} KB)`);
      const token = localStorage.getItem('token');
      const a = document.createElement('a');
      a.href = `/files/transfer/${event.transfer_id}?token=${token}`;
      a.download = event.filename; a.click();
      setTimeout(() => api.delete(`/files/transfer/${event.transfer_id}`).catch(() => {}), 10000);
    }
    if (event.event === 'upload_result') {
      if (event.status === 'ok') setUploadStatus(`Saved to ${event.target_path}`);
      else setUploadStatus('Error: ' + (event.error || 'Unknown'));
    }
  });

  const startScan = async () => {
    setScanning(true); setHosts([]); setSelectedHost(null);
    try {
      await sendCommand(deviceId, 'scan_network', {});
    } catch { setScanning(false); toast.error('Device offline'); }
  };

  const listShares = async (ip) => {
    setSelectedHost(ip);
    setSharesLoading(true);
    setShares([]);
    setBrowsePath('');
    setBrowseEntries([]);
    try {
      await sendCommand(deviceId, 'network/shares', { ip });
    } catch { setSharesLoading(false); toast.error('Device offline'); }
  };

  const browseNetwork = async (path) => {
    setBrowseLoading(true);
    setBrowseError('');
    try {
      await sendCommand(deviceId, 'network/browse', { path });
    } catch { setBrowseLoading(false); toast.error('Device offline'); }
  };

  const downloadNetworkFile = async (path) => {
    setFileStatus('requesting');
    try {
      await api.post(`/cmd/${deviceId}/download_file`, { path });
      setFileStatus('Waiting for agent...');
    } catch (err) {
      setFileStatus('Error: ' + (err.response?.data?.detail || 'Failed'));
    }
  };

  const uploadToNetworkPath = async (e) => {
    e.preventDefault();
    if (!uploadFile || !browsePath) return;
    setUploadStatus('uploading');
    const targetDir = browsePath.endsWith('\\') ? browsePath : browsePath + '\\';
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('target_path', targetDir);
      await api.post(`/cmd/${deviceId}/upload_file`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadStatus('Sent — waiting for agent...');
    } catch (err) {
      setUploadStatus('Error: ' + (err.response?.data?.detail || 'Failed'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2>Network Scanner</h2>
        <button onClick={startScan} disabled={scanning}
          style={{ padding: '7px 16px', background: 'var(--accent)', border: 'none',
                   borderRadius: 6, color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
          {scanning ? 'Scanning…' : 'Scan LAN'}
        </button>
        {hosts.length > 0 && (
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            {hosts.length} hosts discovered
          </span>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: selectedHost ? '1fr 1fr' : '1fr', gap:16 }}>
        {/* Host table */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'left' }}>
                <th style={th}>IP</th>
                <th style={th}>Hostname</th>
                <th style={th}>MAC</th>
                <th style={th}>Ports</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hosts.map((h, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)',
                  background: selectedHost === h.ip ? 'var(--accent)10' : 'transparent' }}>
                  <td style={td}>{h.ip}</td>
                  <td style={td}>{h.hostname || '—'}</td>
                  <td style={{ ...td, color: 'var(--muted)', fontSize: 11 }}>{h.mac || '—'}</td>
                  <td style={td}>
                    {h.ports
                      ? Object.entries(h.ports).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(', ') || '—'
                      : '—'}
                  </td>
                  <td style={td}>
                    {h.ports?.smb && (
                      <button onClick={() => listShares(h.ip)}
                        style={{ padding:'4px 8px', fontSize:11, background:'var(--bg)',
                                 border:'1px solid var(--border)', borderRadius:4,
                                 color:'var(--text)', cursor:'pointer' }}>
                        Browse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hosts.length === 0 && !scanning && (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
              Click Scan LAN to discover devices on the network.
            </div>
          )}
          {scanning && (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
              Scanning subnet… this may take 30–60 seconds.
            </div>
          )}
        </div>

        {/* File browser panel */}
        {selectedHost && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 10, padding:16 }}>
            <div style={{ fontWeight:600, marginBottom:12, color:'var(--muted)',
                          textTransform:'uppercase', fontSize:11, letterSpacing:.5 }}>
              Network Browser — {selectedHost}
            </div>

            {/* Shares */}
            {sharesLoading && <div style={{ color:'var(--muted)', fontSize:12 }}>Loading shares...</div>}
            {shares.length > 0 && !browsePath && (
              <div>
                <div style={{ fontSize:12, color:'var(--muted)', marginBottom:8 }}>
                  {shares.length} share(s) found:
                </div>
                {shares.map((s, i) => (
                  <div key={i} onClick={() => browseNetwork(s.path)}
                    style={{ padding:8, background:'var(--bg)', border:'1px solid var(--border)',
                             borderRadius:6, marginBottom:6, cursor:'pointer', fontSize:12 }}>
                    <span style={{ fontWeight:600 }}>📁 {s.name}</span>
                    <span style={{ color:'var(--muted)', marginLeft:8 }}>{s.path}</span>
                  </div>
                ))}
              </div>
            )}

            {/* File listing */}
            {browsePath && (
              <div>
                <div style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                  <span style={{ fontSize:12, fontFamily:'monospace', color:'var(--accent)', flex:1,
                                 overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {browsePath}
                  </span>
                  <button onClick={() => {
                    const parts = browsePath.replace(/\\$/, '').split('\\');
                    if (parts.length > 3) browseNetwork(parts.slice(0, -1).join('\\'));
                    else { setBrowsePath(''); setBrowseEntries([]); }
                  }} style={{ padding:'4px 8px', fontSize:11, background:'var(--bg)',
                              border:'1px solid var(--border)', borderRadius:4,
                              color:'var(--text)', cursor:'pointer' }}>
                    Up
                  </button>
                  <button onClick={() => { setBrowsePath(''); setBrowseEntries([]); setSelectedHost(null); }}
                    style={{ padding:'4px 8px', fontSize:11, background:'var(--bg)',
                             border:'1px solid var(--border)', borderRadius:4,
                             color:'var(--muted)', cursor:'pointer' }}>
                    Close
                  </button>
                </div>
                {browseError && <div style={{ color:'var(--red)', fontSize:12, marginBottom:8 }}>{browseError}</div>}
                {browseLoading && <div style={{ color:'var(--muted)', fontSize:12 }}>Loading...</div>}
                <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6,
                              maxHeight:400, overflowY:'auto' }}>
                  {browseEntries.map((entry, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px',
                                           borderBottom:'1px solid var(--border)', fontSize:12, cursor:'pointer' }}
                      onClick={() => entry.is_dir ? browseNetwork(entry.path) : downloadNetworkFile(entry.path)}>
                      <span>{entry.is_dir ? '📁' : '📄'}</span>
                      <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {entry.name}
                      </span>
                      {!entry.is_dir && (
                        <span style={{ color:'var(--muted)', fontSize:11, flexShrink:0 }}>
                          {entry.size > 1e6 ? `${(entry.size/1e6).toFixed(1)}MB` :
                           entry.size > 1e3 ? `${(entry.size/1e3).toFixed(1)}KB` : `${entry.size}B`}
                        </span>
                      )}
                    </div>
                  ))}
                  {browseEntries.length === 0 && !browseLoading && !browseError && (
                    <div style={{ padding:20, textAlign:'center', color:'var(--muted)' }}>Empty</div>
                  )}
                </div>

                {/* Upload to this network path */}
                <form onSubmit={uploadToNetworkPath}
                  style={{ display:'flex', gap:8, alignItems:'center', marginTop:8 }}>
                  <input type="file" onChange={e => setUploadFile(e.target.files[0])}
                    style={{ flex:1, fontSize:11, color:'var(--text)' }} />
                  <button type="submit" disabled={!uploadFile || uploadStatus === 'uploading'}
                    style={{ padding:'6px 12px', fontSize:11, background:'var(--accent)',
                             border:'none', borderRadius:4, color:'#fff', cursor:'pointer' }}>
                    {uploadStatus === 'uploading' ? 'Uploading…' : 'Upload Here'}
                  </button>
                </form>
                {uploadStatus && uploadStatus !== 'uploading' && (
                  <div style={{ fontSize:11, marginTop:4, color: uploadStatus.startsWith('Error') ? 'var(--red)' :
                    uploadStatus.startsWith('Saved') ? '#22c55e' : 'var(--muted)' }}>
                    {uploadStatus}
                  </div>
                )}
                {fileStatus && (
                  <div style={{ fontSize:11, marginTop:4, color: fileStatus.startsWith('Error') ? 'var(--red)' :
                    fileStatus.startsWith('Downloaded') ? '#22c55e' : 'var(--muted)' }}>
                    {fileStatus}
                  </div>
                )}
              </div>
            )}

            {!sharesLoading && shares.length === 0 && !browsePath && (
              <div style={{ color:'var(--muted)', fontSize:12, padding:20, textAlign:'center' }}>
                No shares found. The target may require authentication or have sharing disabled.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', fontWeight: 500 };
const td = { padding: '10px 12px', fontSize: 13 };
